import { useState, useEffect, useContext, useCallback } from "react";
import { apiFetch, useAuth } from "../../../../context/AuthContext";
import { useProfile } from "../../../../hooks/useProfile";
import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import useViewType from "../../hooks/useViewType";
import { text } from "./shiftControlText";

const BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error(body?.message || body?.error || `Request failed (${res.status})`);
  }
  return body?.data ?? body;
}

/**
 * Shared state + API calls for the Shift Control modal. Every request carries
 * clientName (the server ignores it for everyone but superadmin) and the
 * caller's view type via apiFetch.
 */
export const useShiftControl = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile, profileLoading } = useProfile();
  const { user, hasFeatureAccess } = useAuth();
  const { isManagerView } = useViewType();

  const t = text[language] || text.en;
  const clientName = profile?.clientName || localStorage.getItem("clientName") || "";

  // Manager view (a manager on their own team) may roster but never define
  // shifts — mirrors the backend MANAGER_ACTIONS whitelist.
  const managerView = user?.role === "manager" && isManagerView;
  const can = {
    manage: !managerView && hasFeatureAccess("attendance", "manage_shifts"),
    assign: hasFeatureAccess("attendance", "assign_shifts"),
  };
  can.any = can.manage || can.assign;

  const [meta, setMeta] = useState({ companies: [], departments: [], savedLocations: {} });
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });

  const notifyError = useCallback((err) => {
    setAlertModal({ open: true, title: t.error, message: err?.message || String(err), variant: "error" });
  }, [t.error]);
  const notifySuccess = useCallback((message) => {
    setAlertModal({ open: true, title: t.success, message, variant: "success" });
  }, [t.success]);

  const withClient = useCallback(
    (params = {}) => new URLSearchParams({ ...(clientName ? { clientName } : {}), ...params }).toString(),
    [clientName],
  );

  const fetchMeta = useCallback(async () => {
    if (!can.any) return;
    try {
      const res = await apiFetch(`${BASE_URL}/api/shifts/meta?${withClient()}`);
      setMeta(await readJson(res));
    } catch (err) {
      notifyError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withClient, can.any, notifyError]);

  const fetchShifts = useCallback(async () => {
    if (!can.any) return;
    try {
      setShiftsLoading(true);
      const res = await apiFetch(`${BASE_URL}/api/shifts?${withClient({ includeInactive: "true" })}`);
      setShifts(await readJson(res));
    } catch (err) {
      notifyError(err);
    } finally {
      setShiftsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withClient, can.any, notifyError]);

  useEffect(() => {
    if (profileLoading) return;
    fetchMeta();
    fetchShifts();
  }, [profileLoading, fetchMeta, fetchShifts]);

  // ── shifts ──
  const saveShift = async (payload, id = null) => {
    const res = await apiFetch(
      id ? `${BASE_URL}/api/shifts/${id}?${withClient()}` : `${BASE_URL}/api/shifts?${withClient()}`,
      { method: id ? "PUT" : "POST", body: JSON.stringify({ ...payload, clientName }) },
    );
    const data = await readJson(res);
    notifySuccess(id ? t.shiftUpdated : t.shiftCreated);
    await fetchShifts();
    return data;
  };

  const setShiftActive = async (id, isActive) => {
    const res = await apiFetch(`${BASE_URL}/api/shifts/${id}/status?${withClient()}`, {
      method: "PATCH",
      body: JSON.stringify({ isActive }),
    });
    await readJson(res);
    await fetchShifts();
  };

  const deleteShift = async (id) => {
    const res = await apiFetch(`${BASE_URL}/api/shifts/${id}?${withClient()}`, { method: "DELETE" });
    await readJson(res);
    notifySuccess(t.shiftDeleted);
    await fetchShifts();
  };

  // ── roster ──
  const fetchRoster = useCallback(async (params) => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    );
    const res = await apiFetch(`${BASE_URL}/api/shifts/roster?${withClient(clean)}`);
    return readJson(res);
  }, [withClient]);

  const saveRoster = async (cells) => {
    const res = await apiFetch(`${BASE_URL}/api/shifts/roster?${withClient()}`, {
      method: "PUT",
      body: JSON.stringify({ cells, clientName }),
    });
    return readJson(res);
  };

  const copyWeek = async ({ infinityCodes, fromWeekStart, toWeekStart }) => {
    const res = await apiFetch(`${BASE_URL}/api/shifts/roster/copy-week?${withClient()}`, {
      method: "POST",
      body: JSON.stringify({ infinityCodes, fromWeekStart, toWeekStart, clientName }),
    });
    return readJson(res);
  };

  return {
    theme, language, t, profileLoading, can,
    meta, shifts, shiftsLoading,
    alertModal, setAlertModal, notifyError, notifySuccess,
    fetchShifts, saveShift, setShiftActive, deleteShift,
    fetchRoster, saveRoster, copyWeek,
  };
};
